import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Repeat, ChevronDown, Calendar, Hash, X } from 'lucide-react';
import type { RecurrenceType, RecurrenceUnit, RecurrenceEndType } from '../../lib/types';

interface RecurrenceConfig {
  is_recurring: boolean;
  recurrence_type?: RecurrenceType;
  recurrence_interval?: number;
  recurrence_unit?: RecurrenceUnit;
  recurrence_end_type?: RecurrenceEndType;
  recurrence_end_date?: string;
  recurrence_max_count?: number;
}

interface RecurrenceSelectorProps {
  value: RecurrenceConfig;
  onChange: (config: RecurrenceConfig) => void;
  disabled?: boolean;
}

const FREQUENCY_OPTIONS: { value: RecurrenceType; label: string }[] = [
  { value: 'daily', label: 'Ogni giorno' },
  { value: 'weekly', label: 'Ogni settimana' },
  { value: 'biweekly', label: 'Ogni 2 settimane' },
  { value: 'monthly', label: 'Ogni mese' },
  { value: 'custom', label: 'Personalizzato' },
];

const UNIT_OPTIONS: { value: RecurrenceUnit; label: string }[] = [
  { value: 'days', label: 'giorni' },
  { value: 'weeks', label: 'settimane' },
  { value: 'months', label: 'mesi' },
];

export function RecurrenceSelector({ value, onChange, disabled }: RecurrenceSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 280;
      let left = rect.left;

      // Ensure dropdown doesn't go off-screen
      if (left + dropdownWidth > window.innerWidth) {
        left = window.innerWidth - dropdownWidth - 16;
      }

      setDropdownPosition({
        top: rect.bottom + 4,
        left: left,
      });
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!buttonRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getLabel = () => {
    if (!value.is_recurring) return 'Non ricorrente';
    if (value.recurrence_type === 'custom' && value.recurrence_interval) {
      const unit = UNIT_OPTIONS.find(u => u.value === value.recurrence_unit);
      return `Ogni ${value.recurrence_interval} ${unit?.label || 'giorni'}`;
    }
    return FREQUENCY_OPTIONS.find(f => f.value === value.recurrence_type)?.label || 'Ricorrente';
  };

  const handleToggle = (enabled: boolean) => {
    if (enabled) {
      onChange({
        is_recurring: true,
        recurrence_type: 'weekly',
        recurrence_end_type: 'never',
      });
    } else {
      onChange({
        is_recurring: false,
        recurrence_type: undefined,
        recurrence_interval: undefined,
        recurrence_unit: undefined,
        recurrence_end_type: undefined,
        recurrence_end_date: undefined,
        recurrence_max_count: undefined,
      });
    }
  };

  const handleFrequencyChange = (type: RecurrenceType) => {
    if (type === 'custom') {
      onChange({
        ...value,
        recurrence_type: type,
        recurrence_interval: value.recurrence_interval || 1,
        recurrence_unit: value.recurrence_unit || 'days',
      });
    } else {
      onChange({
        ...value,
        recurrence_type: type,
        recurrence_interval: undefined,
        recurrence_unit: undefined,
      });
    }
  };

  const handleEndTypeChange = (endType: RecurrenceEndType) => {
    onChange({
      ...value,
      recurrence_end_type: endType,
      recurrence_end_date: endType === 'on_date' ? value.recurrence_end_date : undefined,
      recurrence_max_count: endType === 'after_count' ? (value.recurrence_max_count || 5) : undefined,
    });
  };

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className="fixed w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-700 z-[9999]"
      style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
    >
      {/* Header con toggle */}
      <div className="flex items-center justify-between p-3 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
        <span className="text-sm font-medium text-[#37352F] dark:text-gray-100">Ricorrenza</span>
        <div
          onClick={() => handleToggle(!value.is_recurring)}
          className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
            value.is_recurring ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <div
            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              value.is_recurring ? 'translate-x-4' : ''
            }`}
          />
        </div>
      </div>

      {value.is_recurring && (
        <>
          {/* Frequenza */}
          <div className="p-3 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700">
            <label className="block text-xs text-[#787774] mb-2">Frequenza</label>
            <div className="grid grid-cols-2 gap-1.5">
              {FREQUENCY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleFrequencyChange(option.value)}
                  className={`px-2 py-1.5 text-xs rounded-md transition-colors text-left ${
                    value.recurrence_type === option.value
                      ? 'bg-blue-500 text-white'
                      : 'bg-[#F7F6F3] dark:bg-gray-700 text-[#37352F] dark:text-gray-100 hover:bg-[#EAEAEA] dark:hover:bg-gray-600'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Custom interval */}
            {value.recurrence_type === 'custom' && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-[#787774]">Ogni</span>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={value.recurrence_interval || 1}
                  onChange={(e) => onChange({
                    ...value,
                    recurrence_interval: parseInt(e.target.value) || 1
                  })}
                  className="w-14 px-2 py-1 text-xs bg-[#F7F6F3] dark:bg-gray-700 text-[#37352F] dark:text-gray-100 rounded border border-[rgba(55,53,47,0.09)] dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <select
                  value={value.recurrence_unit || 'days'}
                  onChange={(e) => onChange({
                    ...value,
                    recurrence_unit: e.target.value as RecurrenceUnit
                  })}
                  className="flex-1 px-2 py-1 text-xs bg-[#F7F6F3] dark:bg-gray-700 text-[#37352F] dark:text-gray-100 rounded border border-[rgba(55,53,47,0.09)] dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {UNIT_OPTIONS.map((unit) => (
                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Fine ricorrenza */}
          <div className="p-3">
            <label className="block text-xs text-[#787774] mb-2">Termina</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={value.recurrence_end_type === 'never'}
                  onChange={() => handleEndTypeChange('never')}
                  className="w-3.5 h-3.5 text-blue-500"
                />
                <span className="text-xs text-[#37352F] dark:text-gray-100">Mai</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={value.recurrence_end_type === 'on_date'}
                  onChange={() => handleEndTypeChange('on_date')}
                  className="w-3.5 h-3.5 text-blue-500"
                />
                <span className="text-xs text-[#37352F] dark:text-gray-100">In data</span>
                {value.recurrence_end_type === 'on_date' && (
                  <input
                    type="date"
                    value={value.recurrence_end_date?.split('T')[0] || ''}
                    onChange={(e) => onChange({
                      ...value,
                      recurrence_end_date: e.target.value ? new Date(e.target.value).toISOString() : undefined
                    })}
                    className="flex-1 px-2 py-1 text-xs bg-[#F7F6F3] dark:bg-gray-700 text-[#37352F] dark:text-gray-100 rounded border border-[rgba(55,53,47,0.09)] dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                )}
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={value.recurrence_end_type === 'after_count'}
                  onChange={() => handleEndTypeChange('after_count')}
                  className="w-3.5 h-3.5 text-blue-500"
                />
                <span className="text-xs text-[#37352F] dark:text-gray-100">Dopo</span>
                {value.recurrence_end_type === 'after_count' && (
                  <>
                    <input
                      type="number"
                      min="1"
                      max="999"
                      value={value.recurrence_max_count || 5}
                      onChange={(e) => onChange({
                        ...value,
                        recurrence_max_count: parseInt(e.target.value) || 1
                      })}
                      className="w-14 px-2 py-1 text-xs bg-[#F7F6F3] dark:bg-gray-700 text-[#37352F] dark:text-gray-100 rounded border border-[rgba(55,53,47,0.09)] dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <span className="text-xs text-[#787774]">volte</span>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Rimuovi ricorrenza */}
          <div className="px-3 pb-3">
            <button
              type="button"
              onClick={() => {
                handleToggle(false);
                setIsOpen(false);
              }}
              className="w-full px-3 py-1.5 text-xs text-[#787774] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors flex items-center justify-center gap-1"
            >
              <X className="h-3 w-3" />
              Rimuovi ricorrenza
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        title={getLabel()}
        className={`p-1.5 rounded-md transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        } ${value.is_recurring
          ? 'text-blue-500 bg-blue-50 dark:bg-blue-900/20'
          : 'text-[#787774] hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
        }`}
      >
        <Repeat className="h-3.5 w-3.5" />
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
}
